import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, DollarSign, Factory, Store, Phone, Building2, Shield, Briefcase } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const industries = [
  {
    id: 'healthcare',
    icon: Heart,
    name: 'Healthcare',
    introParagraph: 'The healthcare industry is undergoing a profound transformation, driven by the need for more efficient operations, personalized patient care, and accelerated research. Agility leverages AI to address these critical demands, enhancing clinical outcomes, optimizing administrative processes, and enabling data-driven medical advancements.',
    painPoints: [
      'Inefficient patient scheduling and resource allocation.',
      'Manual data entry and administrative burdens leading to burnout.',
      'Challenges in early disease detection and personalized treatment planning.',
      'Fragmented patient data across disparate systems.'
    ],
    solutions: [
      {
        title: 'AI-Powered Diagnostics & Treatment Planning',
        description: 'Utilize AI to analyze medical images, patient records, and genomic data for faster, more accurate diagnoses and highly personalized treatment recommendations, improving patient outcomes by up to 25%.'
      },
      {
        title: 'Intelligent Hospital Operations & Resource Management',
        description: 'Optimize patient flow, staff scheduling, and bed allocation with AI, reducing wait times by 40% and improving operational efficiency by 30%.'
      },
      {
        title: 'Secure & Unified Health Data Platforms',
        description: 'Build robust, compliant data lakes and AI knowledge hubs for healthcare data, enabling secure, real-time access to patient information and supporting advanced research while ensuring HIPAA compliance.'
      }
    ]
  },
  {
    id: 'financial-services',
    icon: DollarSign,
    name: 'Financial Services & Fintech',
    introParagraph: 'The financial sector demands precision, security, and agility. Agility provides AI solutions that enhance fraud detection, personalize customer experiences, automate compliance, and optimize investment strategies, driving profitability and mitigating risk in a highly competitive landscape.',
    painPoints: [
      'Escalating financial fraud and cybersecurity threats.',
      'Manual, time-consuming compliance and regulatory reporting.',
      'Inefficient customer service and high churn rates.',
      'Suboptimal investment decisions due to data overload.'
    ],
    solutions: [
      {
        title: 'AI-Driven Fraud Detection & Risk Management',
        description: 'Implement real-time AI systems to detect anomalous transactions and predict potential fraud with 95% accuracy, significantly reducing financial losses and enhancing security.'
      },
      {
        title: 'Automated Compliance & Regulatory Reporting',
        description: 'Leverage AI to automate data collection, analysis, and report generation for regulatory compliance, reducing manual effort by 60% and ensuring audit readiness.'
      },
      {
        title: 'Personalized Customer Engagement & Robo-Advisory',
        description: 'Deploy AI agents and predictive analytics to offer personalized financial advice, product recommendations, and 24/7 customer support, increasing customer satisfaction by 35% and reducing service costs.'
      }
    ]
  },
  {
    id: 'manufacturing',
    icon: Factory,
    name: 'Manufacturing & Supply Chain',
    introParagraph: 'Manufacturers and supply chain operators are constantly seeking ways to improve efficiency, reduce waste, and enhance predictive capabilities. Agility\'s AI solutions optimize production processes, enable predictive maintenance, streamline logistics, and enhance demand forecasting, leading to smarter, more resilient operations.',
    painPoints: [
      'Unplanned downtime and equipment failures.',
      'Inefficient inventory management and overstocking/understocking.',
      'Lack of real-time visibility across complex supply chains.',
      'Quality control issues and product defects.'
    ],
    solutions: [
      {
        title: 'Predictive Maintenance & Quality Control',
        description: 'Implement AI models to predict equipment failures before they occur, reducing downtime by 20% and improving product quality by identifying defects early in the production line.'
      },
      {
        title: 'AI-Optimized Supply Chain & Logistics',
        description: 'Use AI to optimize routing, warehouse operations, and inventory levels, reducing logistics costs by 15% and improving delivery times by 25%.'
      },
      {
        title: 'Smart Factory Automation & Process Optimization',
        description: 'Deploy AI-driven automation to enhance production line efficiency, reduce energy consumption, and optimize resource utilization, leading to a 10% increase in throughput.'
      }
    ]
  },
  {
    id: 'retail',
    icon: Store,
    name: 'Retail & E-commerce',
    introParagraph: 'The retail and e-commerce landscape is fiercely competitive, demanding hyper-personalization, seamless customer experiences, and optimized operations. Agility\'s AI solutions empower retailers to understand customer behavior, personalize recommendations, manage inventory intelligently, and streamline online and in-store experiences.',
    painPoints: [
      'Generic customer experiences and low conversion rates.',
      'Inaccurate demand forecasting leading to stockouts or excess inventory.',
      'Inefficient customer support and high cart abandonment rates.',
      'Challenges in understanding complex customer journeys.'
    ],
    solutions: [
      {
        title: 'AI-Powered Personalization & Recommendation Engines',
        description: 'Develop and integrate AI engines that provide hyper-personalized product recommendations and content, increasing conversion rates by 15% and average order value by 10%.'
      },
      {
        title: 'Intelligent Inventory & Demand Forecasting',
        description: 'Leverage AI to predict demand with greater accuracy, optimizing inventory levels, reducing waste by 20%, and ensuring product availability.'
      },
      {
        title: 'AI Chatbots & Virtual Shopping Assistants',
        description: 'Deploy AI-driven chatbots for 24/7 customer support, answering FAQs, guiding purchases, and reducing customer service costs by 30%.'
      }
    ]
  },
  {
    id: 'telecommunications',
    icon: Phone,
    name: 'Telecommunications',
    introParagraph: 'The telecommunications industry is characterized by massive data volumes, complex network management, and intense competition for customer loyalty. Agility\'s AI solutions optimize network performance, predict customer churn, automate service delivery, and enhance cybersecurity, enabling telcos to deliver superior services and drive growth.',
    painPoints: [
      'Network congestion and service quality issues.',
      'High customer churn and difficulty in retaining subscribers.',
      'Manual and slow fault detection and resolution.',
      'Cybersecurity threats and data breaches.'
    ],
    solutions: [
      {
        title: 'AI-Driven Network Optimization & Predictive Maintenance',
        description: 'Utilize AI to analyze network traffic and predict potential outages, optimizing network performance by 15% and reducing downtime by 20%.'
      },
      {
        title: 'Customer Churn Prediction & Retention',
        description: 'Implement AI models to identify customers at risk of churning, enabling proactive retention strategies that reduce churn rates by 10-15%.'
      },
      {
        title: 'Automated Customer Service & Service Provisioning',
        description: 'Deploy AI agents to handle routine customer inquiries and automate service provisioning, improving response times by 50% and reducing operational costs.'
      }
    ]
  }
];

const Industries = () => {
  return (
    <>
      <SEOHead
        title="Industries We Serve | Agility AI Solutions"
        description="AI and automation solutions for healthcare, finance, manufacturing, retail and more."
        url="https://www.agilitytech.ai/industries"
      />
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          <div className="absolute inset-0 opacity-20">
            <div className="w-full h-full bg-gradient-to-br from-teal-500/20 via-blue-500/20 to-purple-500/20 animate-pulse" />
          </div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center fade-in-up">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              AI Solutions Tailored for Your Industry
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Agility delivers industry-specific AI strategies and solutions that address unique challenges, drive innovation, and unlock competitive advantages for leading enterprises worldwide.
            </p>
            <Link
              to="/contact"
              className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium 
                hover:bg-teal-400 transition-all duration-300 inline-flex items-center space-x-2
                shadow-lg hover:shadow-xl transform hover:-translate-y-1 btn-primary"
            >
              <span>Find Your Industry Solution</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Understanding Your Sector's Unique AI Needs
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              Every industry faces distinct operational complexities, market dynamics, and regulatory landscapes. At Agility, we combine deep AI expertise with profound industry knowledge to develop solutions that are not just technologically advanced, but also contextually relevant and highly impactful for your specific sector. Explore how we empower businesses like yours to thrive in the age of AI.
            </p>
          </div>
        </div>
      </section>

      {/* Industry Sections */}
      {industries.map((industry, index) => (
        <section
          key={industry.id}
          id={industry.id}
          className={`py-20 ${index % 2 === 0 ? 'bg-black' : 'bg-gray-900'}`}
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              {/* Industry Header */}
              <div className="text-center mb-16 fade-in-up">
                <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <industry.icon className="h-8 w-8 text-teal-400" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">{industry.name}</h2>
                <p className="text-xl text-gray-300 leading-relaxed max-w-4xl mx-auto">
                  {industry.introParagraph}
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12">
                {/* Pain Points */}
                <div className="fade-in-up">
                  <h3 className="text-2xl font-bold text-white mb-8 flex items-center">
                    <span className="w-3 h-3 bg-red-400 rounded-full mr-3"></span>
                    Key Challenges
                  </h3>
                  <div className="space-y-4">
                    {industry.painPoints.map((point, idx) => (
                      <div key={idx} className="flex items-start space-x-3 p-4 bg-gray-800/30 rounded-lg">
                        <ArrowRight className="h-5 w-5 text-red-400 mt-1 flex-shrink-0" />
                        <p className="text-gray-300">{point}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Solutions */}
                <div className="fade-in-up">
                  <h3 className="text-2xl font-bold text-white mb-8 flex items-center">
                    <span className="w-3 h-3 bg-green-400 rounded-full mr-3"></span>
                    AI Solutions
                  </h3>
                  <div className="space-y-6">
                    {industry.solutions.map((solution, idx) => (
                      <div key={idx} className="p-6 bg-gray-800/50 rounded-xl border border-gray-700/50 hover:bg-gray-800/70 transition-colors">
                        <h4 className="text-lg font-bold text-white mb-3">{solution.title}</h4>
                        <p className="text-gray-300 leading-relaxed">{solution.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Industry CTA */}
              <div className="text-center mt-12 fade-in-up">
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 bg-teal-500 text-white px-8 py-4 
                    rounded-lg font-medium hover:bg-teal-400 transition-colors btn-primary"
                >
                  <span>Discuss AI for Your {industry.name} Business</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto fade-in-up">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Transform Your Industry with AI?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Partner with Agility to unlock industry-specific AI solutions that drive innovation, efficiency, and competitive advantage. Let's discuss how we can address your unique challenges and opportunities.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1 btn-primary"
            >
              <span>Start Your Industry Transformation</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Industries;